/**
 * Handing a member's work to another account or runtime when it reaches its usage limit.
 *
 * A usage limit is a wait (`shared/protocol/agentState.ts`), and waiting is not the only way out: the work can
 * move to another subscription account of the same runtime, or to another runtime, for the rest of the session.
 * The choice is an override kept on the session — never written to the team's configuration — so the member is
 * back on its own account and runtime the next time it starts.
 *
 * Pure and DOM-free: the host applies a target (`src/acp/agentManager.ts`), Mission Control and Chat build the
 * same picker from the same function, the member editor words its preference with the same sentences.
 */
/** "on the Work account" or "on Codex for now": where a handed-off member runs, said after its name. */
export function handOffWords(h) {
    if (!h)
        return undefined;
    if (h.kind === "account")
        return `on the ${h.accountName?.trim() || h.account || "other"} account`;
    return `on ${h.runtimeLabel} for now`;
}
export const ON_LIMIT_WAIT_LABEL = "Wait for it to lift";
/** The preference as a target the host can apply, or undefined for "wait". */
export function onLimitTarget(pref) {
    if (!pref)
        return undefined;
    if (pref.mode === "account" && pref.account?.trim())
        return { kind: "account", account: pref.account.trim() };
    if (pref.mode === "runtime" && pref.provider?.trim())
        return { kind: "runtime", provider: pref.provider.trim(), ...(pref.model?.trim() ? { model: pref.model.trim() } : {}) };
    return undefined;
}
/** The runtimes a member can be handed to: those that start from a runtime id alone (no model server, command or project to name). */
export const HAND_OFF_RUNTIME_IDS = ["copilot", "claude-code", "codex", "gemini", "opencode", "grok", "kilo", "cline"];
/**
 * The picker's rows: first the other accounts of the same runtime that are signed in and not themselves at
 * their limit, then the other runtimes that are installed, switched on and signed in. A runtime row says which
 * model it would use: the member's own on the same runtime, else the runtime's default.
 */
export function buildHandOffOptions(input) {
    const out = [];
    const nameOf = (id) => input.runtimes[id]?.label ?? input.runtimeLabel?.(id) ?? id;
    if (input.provider) {
        for (const a of input.accounts) {
            if (a.runtime !== input.provider || a.slug === (input.account || "default"))
                continue;
            if (a.auth === "signed-out")
                continue;
            if (input.accountLimited?.(a.runtime, a.slug))
                continue;
            out.push({ id: `account:${a.slug}`, label: `${a.name} account`, detail: `Stays on ${nameOf(a.runtime)}`, target: { kind: "account", account: a.slug } });
        }
    }
    for (const id of HAND_OFF_RUNTIME_IDS) {
        if (id === input.provider)
            continue;
        const r = input.runtimes[id];
        if (!r || r.available !== true || r.enabled === false || r.auth?.state === "signed-out")
            continue;
        if (input.runtimeLimited?.(id))
            continue;
        out.push({ id: `runtime:${id}`, label: nameOf(id), detail: "Its default model", target: { kind: "runtime", provider: id } });
    }
    return out;
}
/** The sentence a member editor shows for a preference. */
export function onLimitWords(pref, names = {}) {
    if (!pref || pref.mode === "wait")
        return ON_LIMIT_WAIT_LABEL;
    if (pref.mode === "account")
        return `Hand off to the ${names.account?.(pref.account ?? "") ?? pref.account ?? "other"} account`;
    const runtime = names.runtime?.(pref.provider ?? "") ?? pref.provider ?? "another runtime";
    return `Hand off to ${runtime}${pref.model ? ` · ${pref.model}` : ""}`;
}
/** The audit row's and the activity line's sentence for a hand-off. */
export function handOffSentence(name, to, why) {
    return why === "preference" ? `${name} reached its usage limit and was handed off to ${to}, as set for this member.` : `${name} was handed off to ${to}.`;
}
//# sourceMappingURL=handOff.js.map